import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { fetchExamSets, ExamSetSummary } from '../utils/questionApi';
import { ExamInfo, getExamById, getSessionExamIds, allowedSetNumbers } from '../data/examCatalog';

const TOPIC_EXAMS = ['saa', 'sap', 'ccp', 'dva'];

const ExamCard: React.FC<{ exam: ExamInfo }> = ({ exam }) => {
  const [sets, setSets] = useState<ExamSetSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchExamSets(exam.id).then(res => { if (!cancelled) setSets(res.success && res.sets ? res.sets : []); });
    return () => { cancelled = true; };
  }, [exam.id]);

  const unlocked = sets ? allowedSetNumbers(sets.map(x => x.set)) : [];
  const open = sets ? sets.filter(x => unlocked.includes(x.set)) : [];
  const total = open.reduce((n, x) => n + x.count, 0);
  const hasQuestions = sets !== null && sets.length > 0;

  return (
    <div style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius)', padding: '24px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>{exam.emoji}</div>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>{exam.short} Exam</h3>
      <p style={{ fontSize: '0.9rem', color: 'var(--gray-500)', marginBottom: '6px' }}>{exam.name}</p>
      <p style={{ fontSize: '0.8rem', fontWeight: 700, color: hasQuestions || sets === null ? 'var(--orange)' : 'var(--gray-400)', marginBottom: '16px', minHeight: '1.2em' }}>
        {sets === null ? 'Loading sets…' : hasQuestions ? `${open.length === sets.length ? `${sets.length} ${sets.length === 1 ? 'set' : 'sets'}` : `${open.length} of ${sets.length} sets unlocked`} · ${total} questions` : 'Question sets coming soon'}
      </p>
      {sets && open.length < sets.length && (
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
          {sets.map(s => unlocked.includes(s.set) ? (
            <Link key={s.set} to={`/exam?exam=${exam.key}&mode=practice&set=${s.set}`} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, background: 'var(--blue-pale)', color: 'var(--blue)' }}>Set {s.set}</Link>
          ) : (
            <span key={s.set} title="Not included in your plan" style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, background: 'var(--gray-100)', color: 'var(--gray-400)', cursor: 'not-allowed' }}>🔒 Set {s.set}</span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <Link to={`/exam?exam=${exam.key}&mode=practice`} className="btn btn-orange btn-sm">Practice</Link>
        <Link to={`/exam?exam=${exam.key}&mode=exam`} className="btn btn-blue btn-sm">Take Exam</Link>
        {TOPIC_EXAMS.includes(exam.key) && (
          <Link to={`/aws-exam-questions-${exam.key}`} className="btn btn-outline-blue btn-sm">By Topic</Link>
        )}
      </div>
      {exam.program && (
        <Link to={exam.program.path} style={{ display: 'inline-block', marginTop: '14px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--blue)' }}>
          📚 View {exam.program.label} program →
        </Link>
      )}
    </div>
  );
};

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('userLoggedIn') === '1';
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const isLoggedIn = sessionStorage.getItem('userLoggedIn') === '1';
  const dashUser = sessionStorage.getItem('dashUser') || 'User';

  if (!isLoggedIn) {
    return null;
  }

  // Exams assigned to this user by the admin (Admin > Users & Access)
  const exams = getSessionExamIds().map(id => getExamById(id)).filter((e): e is ExamInfo => !!e);

  return (
    <Layout>
      <div style={{ padding: '40px 5%', background: 'var(--gray-50)', minHeight: 'calc(100vh - 64px)' }}>
        <div className="container" style={{ maxWidth: '1100px' }}>
          <div style={{ background: 'linear-gradient(135deg, var(--blue-pale), var(--orange-pale))', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '32px', marginBottom: '24px', textAlign: 'center' }}>
            <div style={{ fontSize: '2.4rem', marginBottom: '8px' }}>🎉</div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '6px' }}>Welcome, <span style={{ color: 'var(--blue)' }}>{dashUser}</span>!</h1>
            <p style={{ color: 'var(--gray-600)' }}>
              {exams.length > 0 ? `You have access to ${exams.length} ${exams.length === 1 ? 'exam' : 'exams'}. Happy learning!` : 'No exams are assigned to your account yet.'}
            </p>
          </div>

          {exams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius)', color: 'var(--gray-600)' }}>
              Please contact the administrator to get an exam assigned to your account.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: '20px', marginBottom: '28px' }}>
              {exams.map(exam => <ExamCard key={exam.id} exam={exam} />)}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default UserDashboard;
